import {collectAccessoryTemplates} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessories"
import {getMaterialArticleId, resolveMaterialNodeToId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/materials"
import {AssetType, UsedElements} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {FindMaterial, ImageRgba, MaterialReference, TemplateGraph, TemplateNode} from "@cm/template-nodes"
import {TraversalAction} from "@cm/graph-v3"
import {linearColorToSrgbHex} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/color"
import {GetMaterialTagsGQL} from "@app/common/components/viewers/configurator/extensions/bd-cloud/bd-cloud.generated"

type UsedAsset = UsedElements["assets"][number]

async function resolveAssetType(colormassId: string, context: ExtensionContext): Promise<AssetType> {
    const query = context.injector.get(GetMaterialTagsGQL)
    const result = await fetchThrowingErrors(query)({materialId: colormassId})
    const tags = result?.configuratorMaterial?.tagNames?.map((name: string) => name.toLowerCase()) ?? []
    return tags.includes("quilt") ? "Quilt" : "Fabric"
}

/**
 * Colors are always represented by an ImageRGBA node for BD.
 * */
function collectColors(templateGraph: TemplateGraph): string[] {
    const colors = new Set<string>()
    templateGraph.depthFirstTraversalPreorder((node) => {
        if (node instanceof ImageRgba) {
            const color = node.parameters.color
            if (Array.isArray(color)) {
                colors.add(linearColorToSrgbHex(color))
            }
        }
        return TraversalAction.Continue
    })
    return [...colors]
}

function collectMaterialNodes(templateGraph: TemplateGraph): TemplateNode[] {
    const nodes: TemplateNode[] = []
    templateGraph.depthFirstTraversalPreorder((node) => {
        if (node instanceof FindMaterial || node instanceof MaterialReference) {
            nodes.push(node)
        }
        return TraversalAction.Continue
    })
    return nodes
}

async function _collectAccessoryAssets(context: ExtensionContext, seen: Set<string>, errors: string[]): Promise<UsedAsset[]> {
    const templates = await collectAccessoryTemplates(context, errors)
    const assets: UsedAsset[] = []

    for (const {type, colormassId} of templates) {
        if (!colormassId || seen.has(colormassId)) continue
        seen.add(colormassId)
        assets.push({type: type as AssetType, colormassId})
    }

    return assets
}

/**
 * Returns assets and colors used in a template.
 *
 * Assets covers materials and quilts found by traversing the template graph for FindMaterial and MaterialReference nodes.
 * Accessories (Piping, Handle, Zipper, Tape, Puller) are collected from the mattress's accessory tree.
 * The asset type (Fabric or Quilt) is determined by checking the material's tags.
 * Colors are collected from all ImageRgba nodes in the template tree (used in OverlayMaterialColor overlays and zipper template inputs).
 */
export async function getUsedElements(templateGraph: TemplateGraph, context: ExtensionContext): Promise<UsedElements> {
    const materialNodes = collectMaterialNodes(templateGraph)

    const assets: UsedAsset[] = []
    const errors: string[] = []
    const seen = new Set<string>()
    for (const node of materialNodes) {
        try {
            const colormassId = await resolveMaterialNodeToId(node, context)
            if (!colormassId || seen.has(colormassId)) continue
            seen.add(colormassId)
            const type = await resolveAssetType(colormassId, context)
            const bdId = type !== "Quilt" ? await getMaterialArticleId(node, context) : undefined
            assets.push({type, colormassId, bdId})
        } catch (e) {
            errors.push(`Failed to resolve material asset: ${e instanceof Error ? e.message : String(e)}`)
        }
    }

    //TODO: Accessories are not processed at the moment in liferay. The puller/zipper-related changes must be considered.
    // const accessoryAssets = await collectAccessoryAssets(context, seen, errors)
    // assets.push(...accessoryAssets)

    let colors: string[] = []
    try {
        colors = collectColors(templateGraph)
    } catch (e) {
        errors.push(`Failed to collect colors: ${e instanceof Error ? e.message : String(e)}`)
    }

    return {assets, colors, errors}
}
